import * as React from 'react'
import {
  TimeField as AriaTimeField,
  type TimeFieldProps as AriaTimeFieldProps,
  type TimeValue,
} from 'react-aria-components'
import { cn } from '../../lib/utils'
import { DateInput } from './date-field'

/**
 * TimeField — a segmented time input (hour · minute · AM/PM) built on
 * react-aria-components. The keyboard-first counterpart to a clock picker: type
 * or arrow each segment. Reuses `DateInput`/`DateSegment` from `DateField`, so
 * the box and focused-segment styling match the date controls exactly.
 *
 *   <TimeField aria-label="Start" defaultValue={new Time(9, 0)} />
 *
 * `granularity="second"` adds a seconds segment; `hourCycle={24}` switches to
 * 24-hour. Boolean props follow DS naming (`disabled`/`readOnly`/`required`/
 * `invalid`) and map to react-aria's `is*`.
 */

type FieldSize = 'sm' | 'md' | 'lg'
type FieldStatus = 'default' | 'error' | 'success'

export interface TimeFieldProps extends Omit<
  AriaTimeFieldProps<TimeValue>,
  'isDisabled' | 'isReadOnly' | 'isRequired' | 'isInvalid' | 'className'
> {
  size?: FieldSize
  status?: FieldStatus
  disabled?: boolean
  readOnly?: boolean
  required?: boolean
  invalid?: boolean
  className?: string
}

export const TimeField = React.forwardRef<HTMLDivElement, TimeFieldProps>(
  (
    {
      size = 'md',
      status,
      disabled,
      readOnly,
      required,
      invalid,
      className,
      ...props
    },
    ref
  ) => (
    <AriaTimeField
      ref={ref}
      isDisabled={disabled}
      isReadOnly={readOnly}
      isRequired={required}
      isInvalid={invalid}
      className={cn('flex flex-col gap-1.5', className)}
      {...props}
    >
      <DateInput size={size} status={invalid ? 'error' : status} />
    </AriaTimeField>
  )
)
TimeField.displayName = 'TimeField'
